/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable no-confusing-arrow */
import { faCheck, faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect } from 'react';
import { Form, OverlayTrigger, Popover, Table } from 'react-bootstrap';
import { $filter as $filterSignal, $view as $viewSignal } from 'signals/global.signals';
import Pagination from './_components/Pagination';
import TableLoader from './_components/TableLoader';

/**
 * SignalTable component for displaying tabular data with sorting, filtering, and pagination
 * @param {Object} props - Component props
 * @param {Object} props.$filter - Filter signal object
 * @param {Object} props.$view - View signal object
 * @param {function(Object): void} [props.onRowClick] - Callback function when a row is clicked
 * @param {function(Object, number): void} [props.onHeaderClick] - Callback function when a header is clicked
 * @param {Array<Object>} [props.headers] - Array of header objects
 * @param {Array<Object>} [props.rows] - Array of row data objects
 * @param {boolean} [props.hasCheckboxes] - Whether to show checkboxes
 * @param {boolean} [props.canFilterColumns] - Whether columns can be filtered
 * @param {function(string, boolean): void} [props.onColumnToggle] - Callback for column toggle
 * @param {number} [props.totalCount] - Total count of items
 * @param {number} [props.currentPageItemsCount] - Current page items count
 * @param {number} [props.currentPage] - Current page number
 * @param {number} [props.paginationMaxButtonAmount] - Maximum pagination buttons
 * @param {number} [props.itemsPerPageAmount] - Items per page
 * @returns {JSX.Element} The rendered SignalTable component
 */
const SignalTable = ({
  $filter = $filterSignal,
  $view = $viewSignal,
  onRowClick,
  onHeaderClick = () => { },
  headers = [],
  rows = [],
  hasCheckboxes = false,
  canFilterColumns = false,
  onColumnToggle,
  totalCount,
  currentPageItemsCount,
  currentPage,
  paginationMaxButtonAmount = 5,
  itemsPerPageAmount = 10,

}) => {
  const handleToggleColumn = (header) => {
    if (typeof onColumnToggle === 'function') {
      onColumnToggle(header.key, !header.isHidden);
    } else {
      const updatedHeaders = headers.map((h) => (
        h.key === header.key ? { ...h, isHidden: !h.isHidden } : h
      ));
      $view.update({ tableHeaders: updatedHeaders });
    }
  };

  const columnTogglerPopover = (
    <Popover>
      <Popover.Body>
        <div className="text-dark fw-800 p-8">Displayed Columns</div>
        {headers.map((header) => (
          <div
            key={header.key || header.value || Math.random()}
            role="button"
            tabIndex={0}
            className="cursor-pointer p-8 w-100 d-flex justify-content-between table-popover-option"
            onClick={() => handleToggleColumn(header)}
          >
            <div className="me-8">{header.value}</div>
            {!header.isHidden && <FontAwesomeIcon icon={faCheck} color="#41696C" />}
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  useEffect(() => {
    // Get the current URL search parameters
    const urlParams = new URLSearchParams(window.location.search);
    // Extract the parameters you want (e.g., sortKey, sortDirection, page)
    const sortKey = urlParams.get('sortKey');
    const sortDirection = urlParams.get('sortDirection');
    const page = urlParams.get('page') || 1;

    // Update the $filter value based on the URL parameters
    $filter.update({
      page: parseInt(page, 10),
      sortKey,
      sortDirection: sortDirection || undefined, // or default sorting logic
    });
  }, [$filter]);

  return (
    <div className="p-0 overflow-hidden" style={{ borderRadius: 12 }}>
      <Table responsive hover>
        <thead>
          <tr>
            {hasCheckboxes && (
              <td className="border-0 fw-800 py-16" aria-label="Check Box">
                <Form.Check
                  checked={$view.value?.isSelectAllChecked}
                  onChange={(e) => {
                    e.stopPropagation();
                    $view.update({
                      isSelectAllChecked: e.target.checked,
                      selectedItems: e.target.checked ? rows : [],
                    });
                  }}
                />
              </td>
            )}
            {headers.map(({ key, value, sortKey, isHidden, className }, idx) =>
              !isHidden ? (
                <td
                  key={key || value || idx}
                  className={`border-bottom border-gray-800 fw-800 text-nowrap py-16 ${className || 'text-start'} bg-transparent`}
                  role="button"
                  tabIndex={idx}
                  onClick={() => {
                    if (!sortKey) return;
                    const isAscending =
                      $filter?.value?.sortKey === sortKey &&
                      $filter?.value?.sortDirection === 'asc';
                    const isDescending =
                      $filter?.value?.sortKey === sortKey &&
                      $filter?.value?.sortDirection === 'desc';
                    $filter.update({
                      page: 1,
                      sortKey: isAscending ? undefined : sortKey,
                      sortDirection: isAscending
                        ? undefined
                        : isDescending
                          ? 'asc'
                          : 'desc',
                    });
                    const params = new URLSearchParams($filter.value);
                    window.history.pushState(null, '', `?${params.toString()}`);
                    window.localStorage.setItem(
                      'filterQueryString',
                      params.toString(),
                    );
                    onHeaderClick({ key, value, sortKey }, idx);
                  }}
                >
                  <span className={`d-flex align-items-center ${className?.includes('text-center') ? 'justify-content-center' : ''}`}>
                    {sortKey && (
                      <FontAwesomeIcon
                        icon={
                          $filter?.value?.sortKey === sortKey
                            ? $filter?.value?.sortDirection === 'asc'
                              ? faSortUp
                              : faSortDown
                            : faSort
                        }
                        className="my-auto me-2"
                        size="xs"
                      />
                    )}
                    <span className="text-light">{value}</span>
                  </span>
                </td>
              ) : null)}
            {canFilterColumns && (
              <OverlayTrigger rootClose trigger="click" overlay={columnTogglerPopover}>
                <td className="border-0 pe-24 py-16 cursor-pointer" aria-label="Column Filters">
                  <div className="w-100 d-flex justify-content-end">
                    {/* <FontAwesomeIcon icon={faGear} /> */}
                  </div>
                </td>
              </OverlayTrigger>
            )}
          </tr>
        </thead>
        <tbody>
          {!rows.length && !$view.value.isTableLoading && (
            <tr>
              <td className="border-0 w-100 bg-transparent text-white text-center" colSpan={headers.length + (canFilterColumns ? 2 : 1)}>
                No records found
              </td>
            </tr>
          )}
          {$view.value?.isTableLoading && (
            <tr>
              <td colSpan={headers.length + (canFilterColumns ? 2 : 1)} className="text-center border-0 bg-transparent">
                <TableLoader length={itemsPerPageAmount} />
              </td>
            </tr>
          )}
          {!$view.value?.isTableLoading && rows.map((row, rowIdx) => (
            <tr
              key={row.id !== undefined ? row.id : rowIdx}
              onClick={() => onRowClick && onRowClick(row)}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                visibility: $view.value?.isTableLoading ? 'hidden' : 'visible',
              }}
              className="border-bottom border-gray-800"
            >
              {hasCheckboxes && (
                <td className="border-0 py-32 bg-transparent">
                  <Form.Check
                    checked={$view.value?.selectedItems?.some(({ id }) => id === row.id)}
                    onClick={(e) => {
                      e.stopPropagation();
                      $view.update({ isShiftKey: e.shiftKey });
                    }}
                    onChange={(e) => {
                      e.stopPropagation();
                      let selectedItems = $view.value?.selectedItems || [];
                      if (!e.target.checked) {
                        selectedItems = selectedItems.filter(({ id }) => id !== row.id);
                      } else {
                        selectedItems.push(row);
                      }
                      $view.update({ selectedItems, lastCheckedIndex: rowIdx, isShiftKey: false });
                    }}
                  />
                </td>
              )}
              {headers.map(({ key, isHidden }, idx) => (
                !isHidden ? (
                  <td key={key || idx} className={`bg-transparent text-light border-0 py-16 ${idx === headers.length - 1 ? 'text-right' : ''}`}>
                    {typeof row?.[key] === 'function' ? row[key]() : row[key]}
                  </td>
                ) : null
              ))}
              {canFilterColumns && <td />}
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination
        $filter={$filter}
        itemsPerPageAmount={paginationMaxButtonAmount}
        paginationMaxButtonAmount={paginationMaxButtonAmount}
        totalItemsCount={totalCount}
        currentPageItemsCount={currentPageItemsCount || 0}
        currentPage={currentPage}
      />
    </div>
  );
};

export default SignalTable;
